import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import type { Kapitel } from '../types'
import { grannKapitel, laddaKapitel } from '../lib/content'
import { slugForBegrepp } from '../lib/begrepp'
import { hamtaForladdad } from '../lib/forladdat'
import { useSidTitel } from '../lib/seo'
import Flashcards from '../components/Flashcards'
import MarkeradText from '../components/MarkeradText'
import Avsnitt from '../components/Avsnitt'
import Quiz from '../components/Quiz'
import DelaKapitel from '../components/DelaKapitel'
import LyssnaKontroll from '../components/LyssnaKontroll'
import FeedbackRuta from '../components/FeedbackRuta'
import KapitelStatusBadge from '../components/KapitelStatusBadge'
import AplMaterialVy from '../components/AplMaterialVy'

type Flik = 'text' | 'begrepp' | 'instudering' | 'apl'

const BASFLIKAR: { id: Flik; rubrik: string }[] = [
  { id: 'text', rubrik: 'Text' },
  { id: 'begrepp', rubrik: 'Begrepp' },
  { id: 'instudering', rubrik: 'Instuderingsfrågor' },
]

export default function KapitelSida() {
  const { slug } = useParams<{ slug: string }>()
  const { hash } = useLocation()
  // Förrenderingen förladdar kapitlet så att första (och enda) server-rendern
  // har innehållet; i webbläsaren är cachen tom och effekten laddar som vanligt.
  const [kapitel, setKapitel] = useState<Kapitel | null>(() => hamtaForladdad(`kapitel:${slug}`))
  const [saknas, setSaknas] = useState(false)
  const [flik, setFlik] = useState<Flik>('text')
  const [begreppsvy, setBegreppsvy] = useState<'lista' | 'flashcards'>('lista')
  const [delar, setDelar] = useState(false)
  const brodtextRef = useRef<HTMLElement | null>(null)
  useSidTitel(kapitel ? `Kapitel ${kapitel.id}: ${kapitel.titel}` : undefined)

  useEffect(() => {
    let aktiv = true
    setKapitel(null)
    setSaknas(false)
    setFlik('text')
    setBegreppsvy('lista')
    setDelar(false)
    if (!slug) return
    laddaKapitel(slug).then((k) => {
      if (!aktiv) return
      if (k) setKapitel(k)
      else setSaknas(true)
    })
    return () => {
      aktiv = false
    }
  }, [slug])

  // Rulla till avsnittsankaret (teorisektionens Läs mer-länkar pekar på
  // /kapitel/<slug>#<ankare>) — först när kapitlet laddats och renderats,
  // annars finns målet inte i DOM ännu. decodeURIComponent: ankaret kan
  // innehålla å/ä/ö som webbläsaren procentkodar i location.hash.
  useEffect(() => {
    if (!kapitel || !hash) return
    const mal = document.getElementById(decodeURIComponent(hash.slice(1)))
    if (mal) mal.scrollIntoView()
  }, [kapitel, hash])

  if (saknas) {
    return (
      <div className="wrap">
        <div className="dokument">
          <h1>Kapitlet finns inte ännu</h1>
          <p>Det här kapitlet finns inte.</p>
          <Link className="knapp knapp-primar" to="/">
            Till kapitellistan
          </Link>
        </div>
      </div>
    )
  }

  if (!kapitel) return null

  const { forra, nasta } = grannKapitel(kapitel.id)
  // Bara kurser med APL-krav (t.ex. PRAK) har apl_uppgifter — fliken utelämnas
  // helt i stället för att visas tom.
  const flikar =
    kapitel.apl_uppgifter.length > 0
      ? [...BASFLIKAR, { id: 'apl' as const, rubrik: 'APL-uppgifter' }]
      : BASFLIKAR

  return (
    <div className="wrap">
      <header className="kapitelhuvud">
        <div className="kapitelhuvud-rad no-print">
          <nav className="brodsmula" aria-label="Brödsmulor">
            <Link to="/">← Alla kapitel</Link>
          </nav>
          <div className="knapprad">
            <LyssnaKontroll malRef={brodtextRef} nyckel={kapitel.id} aktiv={flik === 'text'} />
            <button type="button" className="knapp knapp-sekundar knapp-liten" onClick={() => setDelar(true)}>
              ↗ Dela
            </button>
            <button type="button" className="knapp knapp-sekundar knapp-liten" onClick={() => window.print()}>
              🖨 Skriv ut
            </button>
          </div>
        </div>
        <span className="kapitel-nr">Kapitel {kapitel.id}</span>
        <KapitelStatusBadge status={kapitel.status} />
        <h1>{kapitel.titel}</h1>
        <div className="larandemal">
          <h2>Efter kapitlet ska du kunna</h2>
          <ul>
            {kapitel.larandemal.map((mal) => (
              <li key={mal}>{mal.replace(/\s*\(F[1-4](,\s*F[1-4])*\)\s*$/, '')}</li>
            ))}
          </ul>
        </div>
      </header>

      <div className="flikrad no-print" role="tablist" aria-label="Kapitlets delar">
        {flikar.map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            id={`flik-${f.id}`}
            aria-selected={flik === f.id}
            aria-controls={`panel-${f.id}`}
            className={flik === f.id ? 'aktiv' : ''}
            onClick={() => setFlik(f.id)}
          >
            {f.rubrik}
          </button>
        ))}
      </div>

      {/* Alla paneler ligger kvar i DOM (dolda med CSS) så att utskriften får med allt. */}
      <div
        id="panel-text"
        role="tabpanel"
        aria-labelledby="flik-text"
        className={`flik-panel${flik === 'text' ? '' : ' dold'}`}
      >
        <article className="brodtext" ref={brodtextRef}>
          <MarkeradText markdown={kapitel.intro} styckeKlass="ingress" />
          {kapitel.avsnitt.map((avsnitt) => (
            <Avsnitt key={avsnitt.rubrik} avsnitt={avsnitt} medAnkare />
          ))}
        </article>
      </div>

      <div
        id="panel-begrepp"
        role="tabpanel"
        aria-labelledby="flik-begrepp"
        className={`flik-panel print-nysida${flik === 'begrepp' ? '' : ' dold'}`}
      >
        <section className="sektion sektion-begrepp" aria-labelledby="begrepp-rubrik">
          <div className="sektion-huvud">
            <span className="sektion-ikon" aria-hidden="true">🔑</span>
            <h2 id="begrepp-rubrik">Begrepp</h2>
          </div>
          <p className="sektion-ingress">
            Kapitlets viktigaste begrepp. Läs dem som lista eller träna med flashcards — vänd
            kortet och se om du kan förklaringen.
          </p>
          <div className="vyvaxlare no-print" role="group" aria-label="Välj vy för begreppen">
            <button type="button" className={begreppsvy === 'lista' ? 'aktiv' : ''} onClick={() => setBegreppsvy('lista')}>
              Lista
            </button>
            <button
              type="button"
              className={begreppsvy === 'flashcards' ? 'aktiv' : ''}
              onClick={() => setBegreppsvy('flashcards')}
            >
              Flashcards
            </button>
          </div>
          {/* Listan ligger alltid i DOM så att utskriften får med den även i flashcard-läge.
              Varje begrepp länkar till sin egen sida (/begrepp/<slug>). */}
          <dl className={`begreppslista${begreppsvy === 'lista' ? '' : ' dold'}`}>
            {kapitel.begrepp.map((b) => {
              const slug = slugForBegrepp(b.begrepp)
              return (
                <div key={b.begrepp}>
                  <dt>{slug ? <Link to={`/begrepp/${slug}`}>{b.begrepp}</Link> : b.begrepp}</dt>
                  <dd>{b.forklaring}</dd>
                </div>
              )
            })}
          </dl>
          {begreppsvy === 'flashcards' && <Flashcards begrepp={kapitel.begrepp} />}
        </section>
      </div>

      <div
        id="panel-instudering"
        role="tabpanel"
        aria-labelledby="flik-instudering"
        className={`flik-panel print-nysida${flik === 'instudering' ? '' : ' dold'}`}
      >
        <section className="sektion sektion-instudering" aria-labelledby="instudering-rubrik">
          <div className="sektion-huvud">
            <span className="sektion-ikon" aria-hidden="true">💬</span>
            <h2 id="instudering-rubrik">Instuderingsfrågor</h2>
          </div>
          <p className="sektion-ingress">
            Frågor som hjälper dig att kontrollera att du förstått texten. Svara med egna ord —
            gärna skriftligt eller tillsammans med en klasskamrat.
          </p>
          <ol className="fragelista">
            {kapitel.instuderingsfragor.map((f) => (
              <li key={f.id}>{f.fraga}</li>
            ))}
          </ol>
        </section>
      </div>

      {/* Ligger under texten på skärm (syns bara på Text-fliken), och efter
          instuderingsfrågorna i utskriften. */}
      <section
        className={`sektion sektion-ovning print-nysida${flik === 'text' ? '' : ' dold'}`}
        aria-labelledby="ovning-rubrik"
      >
        <div className="sektion-huvud">
          <span className="sektion-ikon" aria-hidden="true">✏️</span>
          <h2 id="ovning-rubrik">Övningsuppgifter</h2>
        </div>
        <p className="sektion-ingress">
          Större uppgifter där du använder kapitlets begrepp och modeller. Nivån visar
          ungefärlig svårighetsgrad (E, C eller A). Gör uppgiften först — titta sedan i facit.
        </p>
        <div className="uppgiftslista">
          {kapitel.ovningsuppgifter.map((o) => (
            <div key={o.id} className="uppgiftskort">
              <div className="uppgiftskort-meta">
                <span className={`niva-badge niva-${o.niva}`} title={`Nivå ${o.niva}`}>
                  {o.niva}
                </span>
                {(o.formaga ?? []).map((f) => (
                  <span key={f} className="formaga-chip">
                    {f}
                  </span>
                ))}
              </div>
              <p>{o.uppgift}</p>
              <details className="facit">
                <summary>Visa facit</summary>
                <p>{o.facit}</p>
              </details>
            </div>
          ))}
        </div>
      </section>

      {/* Egen flik (bara när kapitlet har apl_uppgifter, se flikar ovan) —
          inte en sektion under Text: APL-uppgifter hör inte ihop med
          löptexten, och en egen flik gör det tydligt att det är fristående
          uppdrag, inte en fortsättning på övningsuppgifterna. */}
      {kapitel.apl_uppgifter.length > 0 && (
        <div
          id="panel-apl"
          role="tabpanel"
          aria-labelledby="flik-apl"
          className={`flik-panel print-nysida${flik === 'apl' ? '' : ' dold'}`}
        >
          <section className="sektion sektion-apl" aria-labelledby="apl-rubrik">
            <div className="sektion-huvud">
              <span className="sektion-ikon" aria-hidden="true">🧰</span>
              <h2 id="apl-rubrik">APL-uppgifter</h2>
            </div>
            <p className="sektion-ingress">
              {kapitel.apl_uppgifter.length === 1
                ? 'Ett uppdrag att utföra eller undersöka på din arbetsplats.'
                : `${kapitel.apl_uppgifter.length} olika uppdrag att utföra eller undersöka på din arbetsplats — gör var och en för sig.`}{' '}
              Har du ingen plats just nu — gör alternativet i stället, det prövar samma sak.
            </p>
            <div className="apl-lista">
              {kapitel.apl_uppgifter.map((a, i) => (
                <div key={a.id} className="apl-kort">
                  <div className="apl-kort-huvud">
                    <span className="apl-nr" aria-hidden="true">{i + 1}</span>
                    <h3>Uppgift {i + 1} av {kapitel.apl_uppgifter.length}</h3>
                  </div>
                  <p>{a.uppdrag}</p>
                  <p>
                    <span className="apl-etikett">Har du ingen APL-plats just nu?</span>{' '}
                    {a.alternativ_utan_apl}
                  </p>
                  <p>
                    <span className="apl-etikett">Redovisning:</span> {a.redovisning}
                  </p>
                  {a.elevmaterial && <AplMaterialVy material={a.elevmaterial} />}
                  {a.handledarinfo && (
                    <p className="apl-handledarinfo">
                      <span className="apl-etikett">Till din handledare:</span> {a.handledarinfo}
                    </p>
                  )}
                  {a.bedomningsstod && (
                    <details className="facit">
                      <summary>Visa bedömningsstöd</summary>
                      <p>{a.bedomningsstod}</p>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      <section
        className={`sektion sektion-quiz no-print${flik === 'text' ? '' : ' dold'}`}
        aria-labelledby="quiz-rubrik"
      >
        <div className="sektion-huvud">
          <span className="sektion-ikon" aria-hidden="true">🎯</span>
          <h2 id="quiz-rubrik">Quiz</h2>
        </div>
        <p className="sektion-ingress">
          Testa dig själv! Välj det alternativ du tror är rätt — du får en förklaring direkt
          efter varje fråga.
        </p>
        <Quiz key={kapitel.id} fragor={kapitel.quizfragor} />
      </section>

      {flik === 'text' && <FeedbackRuta typ="kapitel" objektId={kapitel.id} benamning="det här kapitlet" />}

      {delar && <DelaKapitel titel={kapitel.titel} onStang={() => setDelar(false)} />}

      <nav className="kapitelnav no-print" aria-label="Kapitelnavigering">
        {forra && forra.slug && (
          <Link to={`/kapitel/${forra.slug}`}>
            <span>← Föregående kapitel</span>
            <strong>
              {forra.id}. {forra.titel}
            </strong>
          </Link>
        )}
        {nasta && nasta.slug && (
          <Link to={`/kapitel/${nasta.slug}`} className="nasta">
            <span>Nästa kapitel →</span>
            <strong>
              {nasta.id}. {nasta.titel}
            </strong>
          </Link>
        )}
      </nav>
    </div>
  )
}
